/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description Lexical Editor
 */
import React from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { ListItemNode, ListNode } from '@lexical/list';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';

import ToolbarPlugin from './plugins/ToolbarPlugin';
import DefaultValuePlugin from './plugins/DefaultValuePlugin';
import { EditorState, LexicalEditor } from 'lexical';

interface IProps {
	editor?: LexicalEditor;
	value?: string | null;
	onChange: (editor: LexicalEditor) => void;
	placeholder?: string;
}

const ExampleTheme = {
	code: 'editor-code',
	heading: {
		h1: 'editor-heading-h1',
		h2: 'editor-heading-h2',
		h3: 'editor-heading-h3',
		h4: 'editor-heading-h4',
		h5: 'editor-heading-h5',
	},
	image: 'editor-image',
	link: 'editor-link',
	list: {
		listitem: 'editor-listitem',
		nested: {
			listitem: 'editor-nested-listitem',
		},
		ol: 'editor-list-ol',
		ul: 'editor-list-ul',
	},
	ltr: 'text-left',
	paragraph: 'editor-paragraph',
	placeholder: 'editor-placeholder',
	quote: 'editor-quote',
	rtl: 'text-right',
	text: {
		bold: 'font-bold',
		code: 'editor-text-code',
		hashtag: 'editor-text-hashtag',
		italic: 'italic',
		overflowed: 'editor-text-overflowed',
		strikethrough: 'line-through',
		underline: 'underline',
		underlineStrikethrough: 'underline line-through',
	},
};

const editorConfig = {
	namespace: 'React.js Demo',
	nodes: [
		HeadingNode,
		ListNode,
		ListItemNode,
		QuoteNode,
		CodeNode,
		CodeHighlightNode,
		TableNode,
		TableCellNode,
		TableRowNode,
		AutoLinkNode,
		LinkNode,
	],
	onError: (error: Error) => {
		throw error;
	},
	theme: ExampleTheme,
};

const Editor: React.FC<IProps> = ({ value, onChange, placeholder = 'Type your comments here...' }) => {
	const handleChange = (__: EditorState, lexEditor: LexicalEditor) => {
		onChange(lexEditor);
	};
	return (
		<LexicalComposer initialConfig={editorConfig}>
			<div className='editor-container'>
				<ToolbarPlugin />
				<div className='editor-inner'>
					<RichTextPlugin
						contentEditable={
							<ContentEditable
								className='editor-input'
								aria-placeholder={placeholder}
								placeholder={<div className='editor-placeholder'>{placeholder}</div>}
							/>
						}
						ErrorBoundary={LexicalErrorBoundary}
					/>
					<OnChangePlugin onChange={handleChange} />
					<DefaultValuePlugin value={value} />
					<AutoFocusPlugin />
					<HistoryPlugin />
					<LinkPlugin />
					<ListPlugin />
				</div>
			</div>
		</LexicalComposer>
	);
};

export default Editor;
